import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { SignedIn, SignedOut } from "@clerk/clerk-react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { publicApi, useApi, formatDate } from "@/lib/api";
import Stars from "@/components/Stars";
import { GoldSignIn } from "@/components/Nav";

function ReviewForm({ eveningId }) {
  const api = useApi();
  const qc = useQueryClient();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) { toast.error("Please choose a star rating"); return; }
    setBusy(true);
    try {
      await api.post(`/evenings/${eveningId}/reviews`, { rating, comment });
      toast.success("Thank you for your note");
      setComment(""); setRating(0);
      qc.invalidateQueries({ queryKey: ["reviews", eveningId] });
      qc.invalidateQueries({ queryKey: ["evening", eveningId] });
    } catch {
      toast.error("Could not submit your review");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="card-parchment p-6 mb-8" data-testid="review-form">
      <div className="eyebrow mb-3">Leave your note</div>
      <div className="flex items-center gap-1.5 mb-4" data-testid="rating-input">
        {[1, 2, 3, 4, 5].map((i) => (
          <button
            key={i}
            type="button"
            data-testid={`rating-star-${i}`}
            onClick={() => setRating(i)}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(0)}
            className="transition-transform hover:scale-110"
          >
            <Star className={`w-7 h-7 ${i <= (hover || rating) ? "fill-gold text-gold" : "text-stone/40"}`} />
          </button>
        ))}
      </div>
      <textarea
        rows={3}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        data-testid="review-comment"
        placeholder="What did the evening feel like? A detail you'll remember…"
        className="w-full bg-parchment-light border border-stone-border p-3 text-sm text-ink outline-none focus:border-gold resize-none mb-4"
      />
      <button type="submit" disabled={busy} data-testid="submit-review-btn" className="btn-gold px-6 py-3 text-sm font-semibold tracking-wide disabled:opacity-60">
        {busy ? "Sending…" : "Share my note"}
      </button>
    </form>
  );
}

export default function ReviewSection({ eveningId }) {
  const { data: reviews = [], isLoading } = useQuery({
    queryKey: ["reviews", eveningId],
    queryFn: async () => (await publicApi.get(`/evenings/${eveningId}/reviews`)).data,
  });
  const avg = reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0;

  return (
    <div data-testid="reviews-section">
      <div className="flex items-center justify-between mb-6">
        <div className="eyebrow">Notes from the table</div>
        {reviews.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-ink-muted" data-testid="reviews-avg">
            <Stars value={avg} size={16} />
            <span>{avg.toFixed(1)} · {reviews.length} note{reviews.length > 1 ? "s" : ""}</span>
          </div>
        )}
      </div>

      <SignedIn><ReviewForm eveningId={eveningId} /></SignedIn>
      <SignedOut>
        <div className="card-parchment p-6 mb-8 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-ink-muted">Been to this evening? Sign in to leave a note.</p>
          <GoldSignIn><button className="btn-gold px-5 py-2.5 text-sm font-semibold tracking-wide" data-testid="review-signin-btn">Sign in</button></GoldSignIn>
        </div>
      </SignedOut>

      {isLoading ? (
        <p className="text-stone text-sm">Loading notes…</p>
      ) : reviews.length === 0 ? (
        <p className="text-ink-muted text-sm italic">No notes yet — be the first to describe the evening.</p>
      ) : (
        <div className="space-y-5" data-testid="reviews-list">
          {reviews.map((r) => (
            <div key={r.id} className="border-b border-stone-border/60 pb-5" data-testid={`review-${r.id}`}>
              <div className="flex items-center gap-3 mb-2">
                {r.user_avatar
                  ? <img src={r.user_avatar} alt="" className="w-9 h-9 rounded-full object-cover" />
                  : <div className="w-9 h-9 rounded-full bg-wine/15 flex items-center justify-center font-serif text-wine">{(r.user_name || "G")[0]}</div>}
                <div>
                  <div className="text-sm font-medium text-ink">{r.user_name}</div>
                  <div className="flex items-center gap-2">
                    <Stars value={r.rating} size={12} />
                    <span className="text-xs text-stone">{formatDate(r.created_at)}</span>
                  </div>
                </div>
              </div>
              {r.comment && <p className="text-ink-muted text-sm leading-relaxed pl-12">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
